import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Flex, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getAppliedFilters,
  getBlockDetails,
  getBlockDimensions,
  getIndicatorTableData,
  getSelectedBlockDimension,
  handleFilters,
  resetSelectedBlockDimension,
  setSelectedBlockDimensionDirect,
} from 'redux/PlanPageSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { AppDispatch, getActivePlanFilterSource } from 'utils/GlobalHelpers';

import {
  clearPlanFiltersFromIndicatorInputPanel,
  syncPlanFiltersToIndicatorInputPanel,
} from '../planPageInputPanelFilters';
import {
  buildActiveDimFilterSubsetsFromDraft,
  buildDraftFromFilterSource,
  countActiveFilterItems,
  selectionsToRedux,
} from '../planDimSelectionUtils';
import { PLAN_FILTER_BADGE_BG, PLAN_TEXT } from '../planPageRevampUi';
import { PlanToolbarChevronDownIcon, PlanToolbarFilterIcon } from '../icons';

import PlanDimSelectionModal, { type PlanDimensionOption } from './PlanDimSelectionModal';

import type { PlanDimDraftSelections } from '../planDimSelectionUtils';

interface Properties {
  onApplyFilters?: () => void;
}

const PlanFilterByControl = ({ onApplyFilters }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const blockDimensions = useSelector(getBlockDimensions);
  const selectedBlockDimension = useSelector(getSelectedBlockDimension);
  const appliedFilters = useSelector(getAppliedFilters);
  const blockDetails = useSelector(getBlockDetails);
  const indicatorTableData = useSelector(getIndicatorTableData);
  const themeColors = useSelector(getPlanPageThemeColors);

  const [isOpen, setIsOpen] = useState(false);
  const [activeDimensionName, setActiveDimensionName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [draftSelections, setDraftSelections] = useState<PlanDimDraftSelections>({});

  const dimensions = useMemo<PlanDimensionOption[]>(
    () =>
      (blockDimensions?.dimensions ?? []).map((dimension: any) => ({
        id: dimension.id,
        name: dimension.name,
        items: dimension.items ?? [],
      })),
    [blockDimensions?.dimensions],
  );

  const visibleDimensions = useMemo(
    () => dimensions.filter((dimension) => dimension.items.length > 0),
    [dimensions],
  );

  const activeFilterCount = useMemo(() => {
    const source = getActivePlanFilterSource(
      appliedFilters,
      selectedBlockDimension,
      blockDimensions,
    );
    if (!source) return 0;
    return countActiveFilterItems(source);
  }, [appliedFilters, blockDimensions, selectedBlockDimension]);

  const persistFilters = useCallback(
    (selections: Record<string, string[]>[]) => {
      if (!blockDetails?.model_id || !blockDetails?.id) return;
      try {
        const planFilters = localStorage.getItem('global-modal-filters');
        const data: Record<string, any> = planFilters ? JSON.parse(planFilters) : {};
        const modelId = String(blockDetails.model_id);
        const currentBlockId = String(blockDetails.id);
        const modelData: Record<string, any> = data[modelId] || {};
        const blockFilters: any = modelData[currentBlockId] || {};

        if (selections.length > 0) {
          blockFilters.selectedBlockDimension = selections;
        } else {
          delete blockFilters.selectedBlockDimension;
        }

        modelData[currentBlockId] = blockFilters;
        data[modelId] = modelData;
        localStorage.setItem('global-modal-filters', JSON.stringify(data));
      } catch {
        // Ignore localStorage write failures
      }
    },
    [blockDetails?.id, blockDetails?.model_id],
  );

  const applyIndicatorTableFilters = useCallback(() => {
    if (!indicatorTableData?.dimensions) return;
    syncPlanFiltersToIndicatorInputPanel(dispatch, {
      appliedFilters,
      blockDimensions,
      indicatorDimensions: indicatorTableData.dimensions,
    });
  }, [appliedFilters, blockDimensions, dispatch, indicatorTableData?.dimensions]);

  useEffect(() => {
    applyIndicatorTableFilters();
  }, [applyIndicatorTableFilters]);

  const resetDraftFromApplied = useCallback(() => {
    const filterSource =
      getActivePlanFilterSource(appliedFilters, selectedBlockDimension, blockDimensions) ??
      selectedBlockDimension;
    setDraftSelections(buildDraftFromFilterSource(filterSource, visibleDimensions));
  }, [appliedFilters, blockDimensions, selectedBlockDimension, visibleDimensions]);

  const openModal = useCallback(() => {
    resetDraftFromApplied();
    setActiveDimensionName(visibleDimensions[0]?.name ?? '');
    setSearchQuery('');
    setIsOpen(true);
  }, [resetDraftFromApplied, visibleDimensions]);

  const handleCloseModal = useCallback(() => {
    resetDraftFromApplied();
    setSearchQuery('');
    setIsOpen(false);
  }, [resetDraftFromApplied]);

  const handleApply = useCallback(() => {
    const payload = selectionsToRedux(draftSelections);
    const activeSubsets = buildActiveDimFilterSubsetsFromDraft(draftSelections, visibleDimensions);
    dispatch(setSelectedBlockDimensionDirect(payload));
    dispatch(handleFilters());
    persistFilters(activeSubsets);

    if (indicatorTableData?.dimensions?.length) {
      syncPlanFiltersToIndicatorInputPanel(dispatch, {
        appliedFilters: payload,
        blockDimensions,
        indicatorDimensions: indicatorTableData.dimensions,
      });
    }

    setIsOpen(false);
    onApplyFilters?.();
  }, [
    blockDimensions,
    dispatch,
    draftSelections,
    indicatorTableData?.dimensions,
    onApplyFilters,
    persistFilters,
    visibleDimensions,
  ]);

  const handleClearAll = useCallback(async () => {
    await dispatch(resetSelectedBlockDimension());
    setDraftSelections(buildDraftFromFilterSource(selectedBlockDimension, visibleDimensions));

    if (blockDetails?.model_id && blockDetails?.id) {
      try {
        const planFilters = localStorage.getItem('global-modal-filters');
        if (planFilters) {
          const data: Record<string, any> = JSON.parse(planFilters);
          const modelId = String(blockDetails.model_id);
          const currentBlockId = String(blockDetails.id);
          const modelData: Record<string, any> = data[modelId] || {};
          if (modelData[currentBlockId]) {
            delete modelData[currentBlockId].selectedBlockDimension;
            data[modelId] = modelData;
            localStorage.setItem('global-modal-filters', JSON.stringify(data));
          }
        }
      } catch {
        // Ignore localStorage clear failures
      }
    }

    setIsOpen(false);
    clearPlanFiltersFromIndicatorInputPanel(dispatch);
    onApplyFilters?.();
  }, [
    blockDetails?.id,
    blockDetails?.model_id,
    dispatch,
    onApplyFilters,
    selectedBlockDimension,
    visibleDimensions,
  ]);

  const handleToggleItem = useCallback(
    (dimensionName: string, itemName: string, checked: boolean) => {
      setDraftSelections((previous) => {
        const currentItems = new Set(previous[dimensionName] ?? []);
        if (checked) {
          currentItems.add(itemName);
        } else {
          currentItems.delete(itemName);
        }
        return {
          ...previous,
          [dimensionName]: Array.from(currentItems),
        };
      });
    },
    [],
  );

  return (
    <>
      <Button
        size='sm'
        h='32px'
        minH='32px'
        px={0}
        py={0}
        w='fit-content'
        {...PLAN_TEXT.body}
        border='none'
        borderRadius='0'
        bg='transparent'
        color={themeColors.textDark}
        display='inline-flex'
        alignItems='center'
        flexWrap='nowrap'
        gap={3}
        lineHeight='1'
        boxShadow='none'
        _hover={{ bg: themeColors.activeBg }}
        _focus={{ boxShadow: 'none' }}
        onClick={openModal}
      >
        <PlanToolbarFilterIcon width={16} height={16} color={themeColors.primary} />
        <Text {...PLAN_TEXT.body} whiteSpace='nowrap' lineHeight='1.2'>
          Filter by
        </Text>
        {activeFilterCount > 0 && (
          <Flex
            minW='18px'
            h='18px'
            px={1}
            borderRadius='full'
            bg={PLAN_FILTER_BADGE_BG}
            color={themeColors.white}
            {...PLAN_TEXT.badge}
            alignItems='center'
            justifyContent='center'
            flexShrink={0}
          >
            {activeFilterCount}
          </Flex>
        )}
        <PlanToolbarChevronDownIcon width={10} height={6} color={themeColors.primary} />
      </Button>

      <PlanDimSelectionModal
        isOpen={isOpen}
        dimensions={dimensions}
        activeDimensionName={activeDimensionName}
        draftSelections={draftSelections}
        searchQuery={searchQuery}
        onClose={handleCloseModal}
        onApply={handleApply}
        onClearAll={handleClearAll}
        onActiveDimensionChange={setActiveDimensionName}
        onSearchChange={setSearchQuery}
        onToggleItem={handleToggleItem}
      />
    </>
  );
};

export default memo(PlanFilterByControl);
